'use client';
import Link from 'next/link';
import Image from 'next/image';
import type { Match } from '@/lib/tourData';

interface MatchCardProps {
  match: Match;
  tourId: string;
}

function TeamRow({ team, innings }: { team: { name: string; logo: string; shortName: string }; innings?: { score: number; wickets: number; overs: string } }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        {team.logo ? (
          <div className="w-7 h-7 rounded-full overflow-hidden bg-rc-border flex-shrink-0">
            <Image src={team.logo} alt={`${team.name} cricket logo`} width={28} height={28} className="w-full h-full object-cover" unoptimized />
          </div>
        ) : (
          <div className="w-7 h-7 rounded-full bg-rc-border flex items-center justify-center flex-shrink-0">
            <span className="text-white/40 text-[9px] font-bold">{team.shortName.slice(0, 3)}</span>
          </div>
        )}
        <span className="text-white/85 text-sm font-medium">{team.name}</span>
      </div>
      {innings ? (
        <span className="text-white text-sm font-semibold">
          {innings.score}/{innings.wickets}
          <span className="text-white/50 text-xs font-normal ml-1">({innings.overs})</span>
        </span>
      ) : (
        <span className="text-white/40 text-xs">Yet to Bat</span>
      )}
    </div>
  );
}

export default function MatchCard({ match, tourId }: MatchCardProps) {
  const isLive = match.status === 'live';
  const isUpcoming = match.status === 'upcoming';
  const isCompleted = match.status === 'completed';
  const isTBC = match.team1.id.startsWith('tbc') || match.team2.id.startsWith('tbc');

  const matchTypeLabel = match.matchType === 'league' ? match.matchNumber
    : match.matchType === 'qualifier' ? 'Qualifier'
    : match.matchType === 'eliminator' ? 'Eliminator'
    : match.matchType === 'semifinal' ? 'Semi Final'
    : match.matchType === 'final' ? 'Final'
    : match.matchNumber;

  return (
    <Link
      href={`/cricket/tour/${tourId}/match/${match.id}`}
      className="block group"
    >
      <div
        className="relative rounded-lg overflow-hidden transition-all duration-200 hover:scale-[1.01] hover:shadow-lg"
        style={{ backgroundColor: 'rgb(18, 23, 33)' }}
      >
        {/* Live indicator bar */}
        {isLive && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-rc-orange" />
        )}

        <div className="p-4">
          {/* Match number */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/50 text-xs font-medium">{matchTypeLabel}</span>
            {isLive && (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-rc-orange animate-pulse" />
                <span className="text-rc-orange text-xs font-bold uppercase tracking-wide">LIVE</span>
              </div>
            )}
          </div>

          {/* Teams */}
          <div className="space-y-2.5">
            <TeamRow team={match.team1} innings={match.innings1} />
            <TeamRow team={match.team2} innings={match.innings2} />
          </div>

          {/* Footer */}
          <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
            {isLive ? (
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-rc-orange" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span className="text-rc-orange text-xs font-bold uppercase tracking-wide">WATCH LIVE</span>
              </div>
            ) : isCompleted ? (
              <div className="flex-1">
                {match.result && (
                  <p className="text-white/60 text-xs leading-relaxed">{match.result}</p>
                )}
                {!match.result && <span className="text-white/40 text-xs">Completed</span>}
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-white/60 text-xs">{match.date}</span>
                <span className="text-white text-sm font-semibold">{match.time}</span>
              </div>
            )}

            {isUpcoming && !isTBC && (
              <div className="text-right">
                <p className="text-white/40 text-[10px]">{match.venue?.split(',')[0]}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
